/**
 * iter86 - Compact "Delete my account" section for the Dashboard footer.
 *
 * Founder feedback: no separate Settings destination. Delete lives on the
 * Dashboard itself, collapsed by default, low visual weight - a disclosure
 * link, not a section header competing with product cards.
 *
 * All logic is identical to the previous Settings.jsx page: password
 * re-entry + type-DELETE confirmation, honest what's-lost copy, 14-day
 * grace with a self-serve cancel link over email. This component is
 * placement only.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

export default function DeleteAccountSection() {
  const nav = useNavigate();
  const [open,     setOpen]     = useState(false);   // disclosure open?
  const [showForm, setShowForm] = useState(false);   // form open?
  const [password, setPassword] = useState("");
  const [typed,    setTyped]    = useState("");
  const [busy,     setBusy]     = useState(false);
  const [status,   setStatus]   = useState(null);

  useEffect(() => {
    api.get("/account/deletion-status").then(r => setStatus(r.data)).catch(() => setStatus(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (typed.trim() !== "DELETE") return;
    setBusy(true);
    try {
      await api.post("/account/delete", { password, confirmation: "DELETE" });
      toast.success("Account deactivated. Check your inbox for the cancellation link.");
      try { await api.post("/auth/logout"); } catch {}
      localStorage.removeItem("moe_token");
      setTimeout(() => nav("/login"), 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
      setBusy(false);
    }
  };

  return (
    <div className="mt-16 pt-8 border-t border-ink/10" data-testid="dash-delete-section">
      {/* Soft-delete already scheduled? Show the calm status banner and nothing else. */}
      {status?.soft_deleted ? (
        <div className="text-[13px] text-ink/70 leading-snug" data-testid="dash-delete-scheduled">
          <b>Your account is scheduled for deletion.</b> Everything is permanently purged on{" "}
          <b>{status.deletion_purge_at?.split("T")[0]}</b>. Use the cancellation link in the
          confirmation email if you change your mind.
        </div>
      ) : (
        <>
          {/* Disclosure: a plain link, not a card. Stays out of the main view. */}
          <button
            data-testid="dash-delete-disclosure"
            onClick={() => setOpen(v => !v)}
            className="inline-flex items-center gap-1.5 text-[12px] text-muted hover:text-ink transition-colors"
          >
            {open ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
            Delete my account
          </button>

          {open && (
            <div className="mt-4 max-w-2xl" data-testid="dash-delete-panel">
              <div className="text-[13px] text-ink/75 leading-snug">
                Your account is deactivated immediately and permanently purged after a 14-day
                grace period. During that window you can cancel from a link we email you. After
                it, this is what goes:
              </div>
              <ul className="mt-2 text-[13px] text-ink/75 space-y-1 list-disc pl-5">
                <li>Your case data, tasks, documents.</li>
                <li>Your Cohort profile and any 1:1 matches / conversations.</li>
                <li>Assemblies you created without a co-organizer.
                    (Assemblies with a co-organizer transfer to them and stay visible to remaining members.)</li>
                <li>Comments you posted stay visible to remaining members, but attributed to "Deleted user".</li>
              </ul>

              {!showForm ? (
                <button
                  data-testid="dash-delete-open-form"
                  onClick={() => setShowForm(true)}
                  className="mt-4 text-[12px] font-semibold text-[color:var(--moe-pink)] hover:underline"
                >
                  Continue to delete my account &rarr;
                </button>
              ) : (
                <form onSubmit={submit} className="mt-4 moe-card p-5 max-w-lg" data-testid="dash-delete-form">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={16} className="text-[color:var(--moe-pink)] shrink-0 mt-0.5"/>
                    <div className="text-[13px] text-ink/80 leading-snug">
                      Confirm your password and type <b>DELETE</b> below. We'll email you a
                      self-serve cancellation link that works for 14 days.
                    </div>
                  </div>
                  <label className="mt-4 block text-[11px] uppercase tracking-widest text-muted font-bold">Password</label>
                  <input
                    data-testid="dash-delete-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="mt-1 moe-input w-full"
                  />
                  <label className="mt-4 block text-[11px] uppercase tracking-widest text-muted font-bold">Type "DELETE"</label>
                  <input
                    data-testid="dash-delete-confirm"
                    type="text"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    required
                    className="mt-1 moe-input w-full"
                  />
                  <div className="mt-5 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => { setShowForm(false); setPassword(""); setTyped(""); }}
                      className="moe-btn moe-btn-ghost"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={busy || typed.trim() !== "DELETE" || !password}
                      data-testid="dash-delete-submit"
                      className="moe-btn moe-btn-primary bg-[color:var(--moe-pink)] hover:bg-[color:var(--moe-pink)]"
                    >
                      {busy ? "Working..." : "Delete my account"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
